/**
 * Authentication Service with Redis session management
 * Business context stored in Redis automatically on login
 */
import http from './httpClient';

class AuthService {
  // Login with email and password
  async signin(credentials) {
    try {
      const response = await http.post('/auth/signin', {
        email: credentials.email,
        password: credentials.password
      });

      if (response.token) {
        // Store both access and refresh tokens
        localStorage.setItem('token', response.token);
        localStorage.setItem('refreshToken', response.refreshToken);
        localStorage.setItem('user', JSON.stringify(response.user));
        
        // Always store businessId for public background image fetch on login/signup pages
        if (response.user.businessId) {
          localStorage.setItem('businessId', String(response.user.businessId));
        }

        // Always set or clear background image based on THIS user's business
        if (response.user.loginBackgroundImage) {
          localStorage.setItem('loginBackgroundImage', response.user.loginBackgroundImage);
        } else {
          // Clear any previous user's background image
          localStorage.removeItem('loginBackgroundImage');
        }
        
        // Business context is now stored in Redis automatically
      }

      return response;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Register new user
  async signup(userData) {
    try {
      const response = await http.post('/auth/signup', {
        name: userData.name,
        email: userData.email,
        password: userData.password,
        confirmPassword: userData.confirmPassword,
        recaptchaToken: userData.recaptchaToken
      });
      
      if (response.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
      }

      return response;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  // Check authentication status
  async checkAuthStatus() {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return { authenticated: false };
      }

      const data = await http.get('/auth/status');
      return data;
    } catch (error) {
      console.error('Auth status check error:', error);
      return { authenticated: false };
    }
  }

  // Logout and clear Redis session
  async logout() {
    try {
      // Call backend logout to clear Redis session
      await http.post('/auth/signout');
    } catch (error) {
      console.warn('Logout request failed:', error);
    } finally {
      // Clear auth and business-specific login branding on sign out
      const bizId = localStorage.getItem('businessId');
      if (bizId) {
        localStorage.removeItem(`onboarding_profile_draft_${bizId}`);
      }
      localStorage.removeItem('onboarding_profile_draft');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('businessId');
      localStorage.removeItem('userId');
      localStorage.removeItem('loginBackgroundImage');
      localStorage.removeItem('rememberedEmail');
    }
  }

  // Get current user
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Get auth token
  getToken() {
    return localStorage.getItem('token');
  }

  // Get refresh token
  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  // Refresh access token using refresh token
  async refreshToken() {
    try {
      const refreshToken = this.getRefreshToken();
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await http.post('/auth/refresh', { refreshToken });
      
      if (response.token) {
        localStorage.setItem('token', response.token);
        localStorage.setItem('user', JSON.stringify(response.user));
        return response.token;
      }
      
      throw new Error('Token refresh failed');
    } catch (error) {
      console.error('❌ Token refresh error:', error);
      // If refresh fails, logout user
      this.logout();
      throw error;
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = localStorage.getItem('token');
    if (!token) return false;

    try {
      // Basic token format check
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      if (payload.exp < currentTime) {
        this.logout();
        return false;
      }

      return true;
    } catch (error) {
      console.warn('Invalid token format:', error);
      this.logout();
      return false;
    }
  }

  // Update user profile
  async updateProfile(profileData) {
    try {
      const token = this.getToken();
      if (!token) throw new Error('Not authenticated');

      const data = await http.put('/auth/profile', profileData);
      
      // Update local storage with new user data
      if (data.user) {
        const currentUser = this.getCurrentUser();
        const updatedUser = { ...currentUser, ...data.user };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Update background image in localStorage if provided
        if (data.user.loginBackgroundImage !== undefined) {
          if (data.user.loginBackgroundImage) {
            localStorage.setItem('loginBackgroundImage', data.user.loginBackgroundImage);
          } else {
            localStorage.removeItem('loginBackgroundImage');
          }
        }
      }
      
      return data;
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  }

  // Update login background image (per-user, saved to Users table)
  async updateBackgroundImage(backgroundImage) {
    try {
      const currentUser = this.getCurrentUser();
      if (!currentUser) throw new Error('Not authenticated');

      const response = await this.updateProfile({
        name: currentUser.name,
        email: currentUser.email,
        phone_number: currentUser.phone || currentUser.phoneNumber,
        loginBackgroundImage: backgroundImage
      });

      return response;
    } catch (error) {
      console.error('Background image update error:', error);
      throw error;
    }
  }

  // Upload background image file to GCS via backend (per-business)
  async updateBusinessBackgroundImage(file) {
    try {
      const token = this.getToken();
      if (!token) throw new Error('Not authenticated');

      // REACT_APP_API_URL already ends with /api (e.g. http://localhost:5000/api)
      const envBase = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
      const raw = (envBase || 'http://localhost:5000/api').trim().replace(/\/+$/, '');
      const apiBase = raw.endsWith('/api') ? raw : `${raw}/api`;

      const formData = new FormData();
      formData.append('backgroundImage', file);

      const response = await fetch(`${apiBase}/auth/business-background/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload background image');
      }

      const data = await response.json();

      // Cache the GCS URL in localStorage for immediate use
      if (data.imageUrl) {
        localStorage.setItem('loginBackgroundImage', data.imageUrl);
      }

      return data;
    } catch (error) {
      console.error('Business background image upload error:', error);
      throw error;
    }
  }

  // Remove business login background image
  async removeBusinessBackgroundImage() {
    try {
      const token = this.getToken();
      if (!token) throw new Error('Not authenticated');

      const data = await http.delete('/auth/business-background');

      localStorage.removeItem('loginBackgroundImage');

      return data;
    } catch (error) {
      console.error('Business background image remove error:', error);
      throw error;
    }
  }

  // Fetch business background image (public, no auth required)
  // Uses direct fetch to avoid httpClient's auth token warnings/redirects on login page
  async getBusinessBackgroundImage(businessId) {
    try {
      if (!businessId) return null;
      
      // Build the API URL the same way httpClient does
      const envBase = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
      const raw = (envBase || '').trim();
      const noTrailingSlash = raw.replace(/\/+$/, '');
      const apiBase = noTrailingSlash.endsWith('/api') ? noTrailingSlash : `${noTrailingSlash}/api`;
      
      const response = await fetch(`${apiBase}/auth/business-background/${businessId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        console.warn('Business background fetch failed:', response.status);
        return null;
      }
      
      const data = await response.json();
      return data?.loginBackgroundImage || null;
    } catch (error) {
      console.error('Business background fetch error:', error);
      return null;
    }
  }

  // Upload profile picture
  async uploadProfilePicture(file) {
    try {
      const token = this.getToken();
      if (!token) throw new Error('Not authenticated');

      const formData = new FormData();
      formData.append('profilePicture', file);

      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${API_BASE}/api/auth/upload-profile-picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload profile picture');
      }

      const data = await response.json();
      
      // Update local storage with new profile picture URL
      const currentUser = this.getCurrentUser();
      if (currentUser) {
        currentUser.profilePicture = data.profilePictureUrl;
        localStorage.setItem('user', JSON.stringify(currentUser));
      }

      return data;
    } catch (error) {
      console.error('Profile picture upload error:', error);
      throw error;
    }
  }

  // Change password
  async changePassword(currentPassword, newPassword) {
    try {
      const token = this.getToken();
      if (!token) throw new Error('Not authenticated');

      const data = await http.put('/auth/change-password', {
        currentPassword,
        newPassword
      });

      if (!data || data.success === false) {
        throw new Error(data?.error || 'Password change failed');
      }

      return data;
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  }

  // Verify current password without altering stored session
  async verifyPassword(password) {
    try {
      const token = this.getToken();
      if (!token) throw new Error('Not authenticated');

      const data = await http.post('/auth/verify-password', { 
        currentPassword: password 
      });

      if (!data || data.success === false) {
        throw new Error(data?.error || 'Invalid password');
      }

      return true;
    } catch (error) {
      console.error('Password verification error:', error);
      throw error;
    }
  }

  // Google Sign-In
  async googleSignIn(credential) {
    try {
      const response = await http.post('/auth/google', {
        credential: credential
      });

      if (response.accessToken) {
        // Store tokens and user data
        localStorage.setItem('token', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken);
        localStorage.setItem('user', JSON.stringify(response.user));
        
      }

      return response;
    } catch (error) {
      console.error('Google Sign-In error:', error);
      throw error;
    }
  }

  // Verify current session (checks if Redis session is still valid)
  async verifySession() {
    try {
      const response = await http.get('/auth/verify');
      return response.valid;
    } catch (error) {
      console.warn('Session verification failed:', error);
      return false;
    }
  }

  // Get business context from session
  async getBusinessContext() {
    try {
      const response = await http.get('/auth/business-context');
      return response.businessId;
    } catch (error) {
      console.warn('Could not get business context:', error);
      return null;
    }
  }
}

export default new AuthService();